import axios from 'axios';
import { apiConfig } from '../config/apiConfig';

const apiClient = axios.create({
  baseURL: apiConfig.baseURL,
  timeout: apiConfig.timeout,
  headers: apiConfig.headers,
});

const PUBLIC_AUTH_ENDPOINTS = [
  '/auth/login/',
  '/auth/forgot-password/',
  '/auth/reset-password/',
  '/auth/refresh/',
];

let refreshRequest = null;

const isPublicAuthRequest = (url = '') => {
  return PUBLIC_AUTH_ENDPOINTS.some((endpoint) =>
    String(url).includes(endpoint),
  );
};

const clearAuthentication = () => {
  localStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token');
  localStorage.removeItem('user');
  localStorage.removeItem('activeRole');
  localStorage.removeItem('school_id');
  localStorage.removeItem('school_profile');
  localStorage.removeItem('school_branding');
};

const redirectToLogin = () => {
  const publicPaths = [
    '/login',
    '/forgot-password',
    '/reset-password',
  ];

  const isAlreadyOnPublicPage = publicPaths.some((path) =>
    window.location.pathname.startsWith(path),
  );

  if (!isAlreadyOnPublicPage) {
    window.location.replace('/login');
  }
};

const refreshAccessToken = async () => {
  const refreshToken = localStorage.getItem('refresh_token');

  if (!refreshToken) {
    throw new Error('Refresh token is missing.');
  }

  const response = await axios.post(
    `${apiConfig.baseURL}/auth/refresh/`,
    {
      refresh: refreshToken,
    },
    {
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      timeout: apiConfig.timeout,
    },
  );

  const newAccessToken =
    response?.data?.access ||
    response?.data?.data?.access;

  if (!newAccessToken) {
    throw new Error(
      'Refresh response did not include an access token.',
    );
  }

  localStorage.setItem('access_token', newAccessToken);

  return newAccessToken;
};

apiClient.interceptors.request.use(
  (config) => {
    const requestConfig = config;

    requestConfig.headers = requestConfig.headers || {};

    const isPublicRequest = isPublicAuthRequest(
      requestConfig.url,
    );

    const token = localStorage.getItem('access_token');

    if (token && !isPublicRequest) {
      requestConfig.headers.Authorization = `Bearer ${token}`;
    } else if (isPublicRequest) {
      delete requestConfig.headers.Authorization;
    }

    const schoolId = localStorage.getItem('school_id');

    if (schoolId && !isPublicRequest) {
      requestConfig.headers['X-School-Id'] = schoolId;
    }

    const isFormData =
      typeof FormData !== 'undefined' &&
      requestConfig.data instanceof FormData;

    if (isFormData) {
      delete requestConfig.headers['Content-Type'];
    } else if (!requestConfig.headers['Content-Type']) {
      requestConfig.headers['Content-Type'] =
        'application/json';
    }

    return requestConfig;
  },
  (error) => Promise.reject(error),
);

apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error?.config;
    const status = error?.response?.status;

    if (
      status !== 401 ||
      !originalRequest ||
      originalRequest._retry ||
      isPublicAuthRequest(originalRequest.url)
    ) {
      return Promise.reject(error);
    }

    originalRequest._retry = true;

    try {
      if (!refreshRequest) {
        refreshRequest = refreshAccessToken().finally(() => {
          refreshRequest = null;
        });
      }

      const newAccessToken = await refreshRequest;

      originalRequest.headers =
        originalRequest.headers || {};

      originalRequest.headers.Authorization =
        `Bearer ${newAccessToken}`;

      return apiClient(originalRequest);
    } catch (refreshError) {
      clearAuthentication();
      redirectToLogin();

      return Promise.reject(refreshError);
    }
  },
);

export {
  clearAuthentication,
};

export default apiClient;